import { createRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Badge } from "../components/ui/Badge/Badge";
import {
	useMailFolders,
	useMailMessages,
	useMailMessage,
	type MailAddress,
	type MailFolder,
} from "../hooks/use-mail";
import { formatDateTime } from "../lib/date";
import { apiUrl } from "../lib/api";
import styles from "./Mail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/mail",
	component: MailPage,
	validateSearch: (search: Record<string, unknown>) => ({
		folder: String(search.folder || "INBOX"),
		page: Number(search.page) || 1,
		uid: search.uid ? String(search.uid) : undefined,
	}),
});

const PAGE_SIZE = 25;

function folderLabel(folder: MailFolder, t: (key: string) => string): string {
	switch (folder.specialUse) {
		case "\\Sent":
			return t("mailSent");
		case "\\Drafts":
			return t("mailDrafts");
		case "\\Junk":
			return t("mailJunk");
		case "\\Trash":
			return t("mailTrash");
		case "\\Archive":
			return t("mailArchive");
		default:
			return folder.path.toUpperCase() === "INBOX" ? t("mailInbox") : folder.name;
	}
}

function addressLabel(addresses: MailAddress[]): string {
	if (!addresses.length) return "-";
	return addresses.map((a) => a.name || a.address || "").join(", ");
}

function MailPage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { folder, page, uid } = route.useSearch();
	const navigate = route.useNavigate();
	const selectedUid = uid ?? null;

	const foldersQuery = useMailFolders();
	const messagesQuery = useMailMessages(folder, page, PAGE_SIZE);
	const messageQuery = useMailMessage(folder, selectedUid);

	useEffect(() => {
		if (messageQuery.data) {
			queryClient.invalidateQueries({ queryKey: ["mail", "list", folder] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [messageQuery.data]);

	const folders = foldersQuery.data?.folders ?? [];
	const messages = messagesQuery.data?.messages ?? [];
	const total = messagesQuery.data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const noAccount = useMemo(() => {
		const err = foldersQuery.error ?? messagesQuery.error;
		return err instanceof Error && err.message.includes("Hesabım") ? err.message : null;
	}, [foldersQuery.error, messagesQuery.error]);

	if (noAccount) {
		return (
			<div>
				<PageHeader title={t("mail")} />
				<Card>
					<div className={styles.emptyState}>
						<p>{noAccount}</p>
						<Link to="/account" className={styles.link}>
							{t("myAccount")}
						</Link>
					</div>
				</Card>
			</div>
		);
	}

	return (
		<div className={styles.page}>
			<PageHeader title={t("mail")} />
			<div className={styles.layout}>
				<Card className={styles.foldersCard ?? ""} bodyClassName={styles.cardBody ?? ""}>
					{foldersQuery.isLoading && <LoadingSpinner size="sm" />}
					{foldersQuery.isError && !noAccount && (
						<ErrorFallback onRetry={foldersQuery.refetch} />
					)}
					{!foldersQuery.isLoading && !foldersQuery.isError && (
						<ul className={styles.folderList}>
							{folders.map((f) => (
								<li key={f.path}>
									<button
										type="button"
										className={[styles.folderItem, f.path === folder && styles.active]
											.filter(Boolean)
											.join(" ")}
										onClick={() =>
											navigate({ search: { folder: f.path, page: 1, uid: undefined } })
										}
									>
										{folderLabel(f, t)}
									</button>
								</li>
							))}
						</ul>
					)}
				</Card>

				<Card className={styles.messagesCard ?? ""} bodyClassName={styles.cardBody ?? ""}>
					{messagesQuery.isLoading && <LoadingSpinner size="sm" />}
					{messagesQuery.isError && !noAccount && (
						<ErrorFallback onRetry={messagesQuery.refetch} />
					)}
					{!messagesQuery.isLoading && !messagesQuery.isError && messages.length === 0 && (
						<p className={styles.emptyMessages}>{t("noEmails")}</p>
					)}
					{!messagesQuery.isLoading && !messagesQuery.isError && messages.length > 0 && (
						<>
							<ul className={styles.messageList}>
								{messages.map((m) => (
									<li key={m.uid}>
										<button
											type="button"
											className={[
												styles.messageItem,
												m.uid === selectedUid && styles.active,
												!m.seen && styles.unseen,
											]
												.filter(Boolean)
												.join(" ")}
											onClick={() =>
												navigate({ search: { folder, page, uid: m.uid } })
											}
										>
											<div className={styles.messageFrom}>{addressLabel(m.from)}</div>
											<div className={styles.messageSubject}>{m.subject}</div>
											<div className={styles.messageDate}>
												{m.date ? formatDateTime(m.date) : "-"}
											</div>
										</button>
									</li>
								))}
							</ul>
							<div className={styles.pager}>
								<button
									type="button"
									disabled={page <= 1}
									onClick={() => navigate({ search: { folder, page: page - 1, uid } })}
								>
									{t("previous")}
								</button>
								<span>
									{page} / {totalPages}
								</span>
								<button
									type="button"
									disabled={page >= totalPages}
									onClick={() => navigate({ search: { folder, page: page + 1, uid } })}
								>
									{t("next")}
								</button>
							</div>
						</>
					)}
				</Card>

				<Card className={styles.viewerCard ?? ""} bodyClassName={styles.cardBody ?? ""}>
					{selectedUid == null && <p className={styles.emptyMessages}>{t("selectEmail")}</p>}
					{selectedUid != null && messageQuery.isLoading && <LoadingSpinner size="sm" />}
					{selectedUid != null && messageQuery.isError && (
						<ErrorFallback onRetry={messageQuery.refetch} />
					)}
					{messageQuery.data && (
						<div className={styles.viewer}>
							<h2 className={styles.viewerSubject}>{messageQuery.data.subject}</h2>
							<div className={styles.viewerMeta}>
								<div>
									<strong>{t("from")}:</strong> {addressLabel(messageQuery.data.from)}
								</div>
								<div>
									<strong>{t("to")}:</strong> {addressLabel(messageQuery.data.to)}
								</div>
								{messageQuery.data.cc.length > 0 && (
									<div>
										<strong>Cc:</strong> {addressLabel(messageQuery.data.cc)}
									</div>
								)}
								<div>
									<strong>{t("date")}:</strong>{" "}
									{messageQuery.data.date ? formatDateTime(messageQuery.data.date) : "-"}
								</div>
							</div>
							{messageQuery.data.attachments.length > 0 && (
								<div className={styles.attachments}>
									{messageQuery.data.attachments.map((a, i) =>
										a.downloadable ? (
											<a
												key={i}
												className={styles.attachmentLink}
												href={apiUrl(
													`/api/mail/messages/${selectedUid}/attachments/${i}?folder=${encodeURIComponent(folder)}`,
												)}
												target="_blank"
												rel="noreferrer"
											>
												<Badge variant="muted">{a.filename}</Badge>
											</a>
										) : (
											<Badge key={i} variant="muted">
												{a.filename}
											</Badge>
										),
									)}
								</div>
							)}
							<div className={styles.viewerDivider} />
							{messageQuery.data.html ? (
								<div
									className={styles.viewerBody}
									dangerouslySetInnerHTML={{ __html: messageQuery.data.html }}
								/>
							) : (
								<pre className={styles.viewerText}>{messageQuery.data.text}</pre>
							)}
						</div>
					)}
				</Card>
			</div>
		</div>
	);
}
